import { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import StatCard from "pages/dashboard-overview/components/StatCard";
import StatCardSkeleton from "pages/analytics/components/StatCardSkeleton";
import SectionPanel from "pages/analytics/components/SectionPanel";
import { ChartSkeleton, ErrorBanner } from "pages/analytics/components/AnalyticsStates";
import { formatNumber, formatPercent } from "pages/analytics/components/analyticsFormatters";
import { getVidalyticsAnalytics } from "services/vidalytics-analytics.service";
import { isCustomRangeIncomplete } from "utils/analyticsQueryParams";

const RetentionTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="bg-white px-2 py-1 border border-gray-200 shadow-sm rounded text-xs">
      {formatPercent(point.viewerPercent)} at {point.progressPercent}%
    </div>
  );
};

function RetentionMiniChart({ points }) {
  const chartData = useMemo(() => {
    const normalized = Array.isArray(points) ? points : [];
    if (normalized.length && typeof normalized[0] === "number") {
      return normalized.map((viewerPercent, index) => ({
        progressPercent: index === 0 ? 0 : [25, 50, 75, 100][index - 1] ?? index * 25,
        viewerPercent,
      }));
    }
    return normalized;
  }, [points]);

  if (!chartData.length) {
    return <p className="text-sm text-gray-500 italic">No retention data available.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={140}>
      <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="retentionFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.25} />
            <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="progressPercent"
          tickFormatter={(value) => `${value}%`}
          tick={{ fontSize: 11 }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis domain={[0, 100]} hide />
        <Tooltip content={<RetentionTooltip />} />
        <Area
          type="monotone"
          dataKey="viewerPercent"
          stroke="#4F46E5"
          strokeWidth={2}
          fill="url(#retentionFill)"
          dot={false}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default function VslAnalyticsSection({ api, filters, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) {
      setIsLoading(false);
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getVidalyticsAnalytics(api, { filters });
        if (!cancelled) setData(result);
      } catch {
        if (!cancelled) {
          setData(null);
          setError("Failed to load VSL analytics.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [api, filters.dateRangeType, filters.customDateRange?.[0], filters.customDateRange?.[1], refreshKey]);

  return (
    <SectionPanel title="VSL Analytics">
      {error && (
        <div className="mb-4">
          <ErrorBanner message={error} />
        </div>
      )}

      {isCustomRangeIncomplete(filters) && !isLoading ? (
        <p className="text-sm text-gray-500 italic">
          Select start and end dates to view VSL analytics.
        </p>
      ) : isLoading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <StatCardSkeleton key={`row1-${i}`} />
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[...Array(2)].map((_, i) => (
              <StatCardSkeleton key={`row2-${i}`} />
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <StatCardSkeleton key={`row3-${i}`} />
            ))}
          </div>
          <ChartSkeleton height={140} />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="VSL Plays"
              value={formatNumber(data?.views)}
              icon="Play"
            />
            <StatCard
              title="Avg % Watched"
              value={formatPercent(data?.avgWatchPercent)}
              icon="Clock"
            />
            <StatCard
              title="VSL Completion Rate %"
              value={formatPercent(data?.completionRate)}
              icon="CheckCircle"
            />
            <StatCard
              title="Drop-off Spike"
              value={
                data?.dropOffSpikePercent != null
                  ? `${formatNumber(data.dropOffSpikePercent)}%`
                  : "-"
              }
              icon="TrendingDown"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <StatCard
              title="Play Events"
              value={formatNumber(data?.playEvents)}
              icon="PlayCircle"
            />
            <StatCard
              title="Pause Events"
              value={formatNumber(data?.pauseEvents)}
              icon="PauseCircle"
            />
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Video Engagement Milestones
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  key: "25",
                  label: "25% Watched",
                  card: "bg-gray-50 border-gray-200",
                  labelColor: "text-gray-500",
                },
                {
                  key: "50",
                  label: "50% Watched",
                  card: "bg-blue-50 border-blue-200",
                  labelColor: "text-blue-600",
                },
                {
                  key: "75",
                  label: "75% Watched",
                  card: "bg-orange-50 border-orange-200",
                  labelColor: "text-orange-600",
                },
                {
                  key: "100",
                  label: "100% Watched",
                  card: "bg-green-50 border-green-200",
                  labelColor: "text-green-600",
                },
              ].map(({ key, label, card, labelColor }) => (
                <div
                  key={key}
                  className={`rounded-lg shadow-sm border p-5 transition-all duration-200 hover:shadow-md ${card}`}
                >
                  <p className={`text-xs font-medium ${labelColor}`}>{label}</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900">
                    {formatNumber(data?.milestones?.[key])}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Retention Curve</h3>
            {data?.retentionCurve?.length ? (
              <RetentionMiniChart points={data.retentionCurve} />
            ) : (
              <p className="text-sm text-gray-500 italic">
                Retention data unavailable — insufficient Vidalytics milestone data for this period.
              </p>
            )}
          </div>
        </div>
      )}
    </SectionPanel>
  );
}
